import type { StreamFormat } from '@auxcast/protocol';

import { buildOpusHead } from './opusHead';
import { deinterleave } from './pcm';
import type { AudioDataLike, AudioDecoderConfigLike } from './webcodecsTypes';
import { getWebCodecs } from './webcodecsTypes';

export type DecoderKind = 'webcodecs' | 'wasm';

export interface OpusAdapterCallbacks {
  /** Planar float samples, one array per channel, in submission order. */
  onAudio(planes: Float32Array[]): void;
  /** Non-recoverable decoder failure; the caller may swap implementations. */
  onError(error: unknown): void;
}

export interface OpusAdapter {
  readonly kind: DecoderKind;
  decode(payload: Uint8Array): void;
  /** Drop decoder state after a discontinuity or an unbridgeable gap. */
  reset(): void;
  close(): void;
}

/** Extract planar float channels from a WebCodecs `AudioData`, then close it. */
export function audioDataToPlanar(data: AudioDataLike): Float32Array[] {
  try {
    const channels = data.numberOfChannels;
    try {
      const planes: Float32Array[] = [];
      for (let c = 0; c < channels; c++) {
        const bytes = data.allocationSize({ planeIndex: c, format: 'f32-planar' });
        const plane = new Float32Array(bytes / Float32Array.BYTES_PER_ELEMENT);
        data.copyTo(plane, { planeIndex: c, format: 'f32-planar' });
        planes.push(plane);
      }
      return planes;
    } catch {
      // Some implementations refuse the planar conversion and only hand back their
      // native interleaved layout; plane 0 then holds every channel.
      const bytes = data.allocationSize({ planeIndex: 0 });
      const interleaved = new Float32Array(bytes / Float32Array.BYTES_PER_ELEMENT);
      data.copyTo(interleaved, { planeIndex: 0 });
      return deinterleave(interleaved, channels);
    }
  } finally {
    data.close();
  }
}

function createWebCodecsAdapter(
  config: AudioDecoderConfigLike,
  frameMs: number,
  callbacks: OpusAdapterCallbacks,
): OpusAdapter {
  const codecs = getWebCodecs();
  if (!codecs) throw new Error('WebCodecs is not available');
  const { AudioDecoder, EncodedAudioChunk } = codecs;

  const frameDurationUs = Math.round(frameMs * 1000);
  let timestampUs = 0;
  let closed = false;

  const build = () => {
    const decoder = new AudioDecoder({
      output: (data) => {
        if (closed) {
          data.close();
          return;
        }
        callbacks.onAudio(audioDataToPlanar(data));
      },
      error: (error) => {
        if (!closed) callbacks.onError(error);
      },
    });
    decoder.configure(config);
    return decoder;
  };

  let decoder = build();

  return {
    kind: 'webcodecs',
    decode(payload) {
      if (closed) return;
      try {
        decoder.decode(
          new EncodedAudioChunk({
            type: 'key',
            timestamp: timestampUs,
            duration: frameDurationUs,
            // Copy: the payload is a view into the socket buffer that the caller
            // reuses as soon as this returns, and decoding is asynchronous.
            data: payload.slice(),
          }),
        );
        timestampUs += frameDurationUs;
      } catch (error) {
        callbacks.onError(error);
      }
    },
    reset() {
      if (closed) return;
      try {
        decoder.reset();
        decoder.configure(config);
      } catch {
        try {
          decoder.close();
        } catch {
          /* already gone */
        }
        decoder = build();
      }
      timestampUs = 0;
    },
    close() {
      closed = true;
      try {
        decoder.close();
      } catch {
        /* already gone */
      }
    },
  };
}

async function createWasmAdapter(
  format: StreamFormat,
  callbacks: OpusAdapterCallbacks,
): Promise<OpusAdapter> {
  const { OpusDecoder } = await import('opus-decoder');
  const decoder = new OpusDecoder({ channels: format.channels });
  await decoder.ready;

  let closed = false;

  return {
    kind: 'wasm',
    decode(payload) {
      if (closed) return;
      try {
        // decodeFrame keeps no reference to the input, but it does need a plain
        // Uint8Array rather than a view with a byte offset into a larger buffer.
        const result = decoder.decodeFrame(
          payload.byteOffset === 0 && payload.byteLength === payload.buffer.byteLength
            ? payload
            : payload.slice(),
        );
        const samples = result.samplesDecoded;
        if (samples <= 0) return;
        callbacks.onAudio(
          result.channelData.map((plane) =>
            plane.length === samples ? plane : plane.slice(0, samples),
          ),
        );
      } catch (error) {
        callbacks.onError(error);
      }
    },
    reset() {
      if (closed) return;
      void decoder.reset().catch(() => undefined);
    },
    close() {
      closed = true;
      try {
        decoder.free();
      } catch {
        /* already freed */
      }
    },
  };
}

/**
 * Pick a decoder for the announced format.
 *
 * WebCodecs is preferred — it is hardware-backed on most platforms and keeps the
 * main thread free. The WASM decoder is the everywhere-fallback, and `preferWasm`
 * exists so the pipeline can switch to it permanently after WebCodecs has failed
 * at runtime rather than looping through a broken decoder.
 */
export async function createOpusAdapter(
  format: StreamFormat,
  callbacks: OpusAdapterCallbacks,
  preferWasm = false,
): Promise<OpusAdapter> {
  if (!preferWasm) {
    const codecs = getWebCodecs();
    if (codecs) {
      const base: AudioDecoderConfigLike = {
        codec: 'opus',
        sampleRate: format.sampleRate,
        numberOfChannels: format.channels,
      };
      const candidates: AudioDecoderConfigLike[] = [
        { ...base, description: buildOpusHead(format.channels) },
        base,
      ];
      for (const config of candidates) {
        try {
          const support = await codecs.AudioDecoder.isConfigSupported(config);
          if (support.supported === false) continue;
          return createWebCodecsAdapter(config, format.frameMs, callbacks);
        } catch {
          continue;
        }
      }
    }
  }
  return createWasmAdapter(format, callbacks);
}
